import { useNavigation } from "@react-navigation/native";
import { observer } from "mobx-react-lite";
import React from "react";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../../core/apis";
import { useStores } from "../../core/stores";
import { Permission } from "../../permissions/types";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { BlockLoading, ModuleBlockSkeleton } from "../../ui/Block";
import { Title } from "../../ui/Title";
import { BusinessAdminTitleAndEmojiEdit } from "./ui/BusinessAdminEmojiPicker";
import { BusinessModuleIcon } from "./ui/BusinessModuleIcon";

export const BusinessModuleUIRenderer = observer(
  ({ uid }: { uid: string; showAddStep?: boolean }) => {
    const { businessModulesStore } = useStores();
    const { businessModulesApi } = useApi();
    const _navigation = useNavigation();
    const businessModule = businessModulesStore.byId.get(uid);

    React.useEffect(() => {
      const unsub = businessModulesApi.watchItem(uid);
      return () => {
        unsub();
      };
    }, [businessModulesApi, uid]);

    if (!businessModule) {
      return <BlockLoading />;
    }

    const isAdmin = businessModule.businessPlan.checkPermission(
      Permission.CanEditModules,
    );

    return (
      <YStack marginBottom="$medium" gap="$medium">
        {!isAdmin && (
          <XStack
            gap={"$medium"}
            alignItems={"center"}
            paddingHorizontal={"$larger"}
            $gtSm={{ paddingHorizontal: 0 }}
          >
            <BusinessModuleIcon businessModule={businessModule} />
            <Title>{businessModule.data.title}</Title>
          </XStack>
        )}

        {isAdmin && (
          <BusinessAdminTitleAndEmojiEdit
            formValues={businessModule.data}
            onChange={(f) => {
              businessModule.updateSettings({
                title: f.title ?? "",
                emoji: f.emoji ?? null,
              });
            }}
          />
        )}

        {businessModule.render()}
      </YStack>
    );
  },
);

/*
{showAddStep && (
            <>
              <Separator />
              <View
                onPress={() => businessModule.businessPlan.setShowAddStepSheet(true)}
                cursor="pointer"
                gap={"$small"}
                style={{ opacity: 0.75 }}
                alignItems="center"
              >
                <XStack gap="$small" alignItems="center">
                  <PlusCircle color={"$muted"} />
                  <Label color={"$muted"}>Add step</Label>
                  <PopoverHelp description="Steps help you plan and make decisions, create a step and set a helpful title and icon to get going" />
                </XStack>
              </View>
            </>
          )}
 */

export const BusinessModuleBlockRenderer = observer(
  ({
    uid,
    active,
    onPress,
  }: {
    uid: string;
    active?: boolean;
    upcoming?: boolean;
    onPress?: () => void;
  }) => {
    const { businessModulesStore } = useStores();
    const { businessModulesApi } = useApi();
    const navigation = usePlanNavigation();

    const businessModule = businessModulesStore.byId.get(uid);

    React.useEffect(() => {
      const unsub = businessModulesApi.watchItem(uid);
      return () => {
        unsub();
      };
    }, [businessModulesApi, uid]);

    if (!businessModule) {
      return <ModuleBlockSkeleton />;
    }

    return (
      <View
        opacity={businessModule.isDone || active ? 1 : 0.25}
        cursor="pointer"
        style={{ flexGrow: 1, width: "100%" }}
        onPress={() => {
          onPress
            ? onPress()
            : navigation.navigate("BusinessTasksModule", {
                businessUid: businessModule.data.businessPlanUid,
                businessModuleUid: businessModule.uid,
              });
        }}
      >
        <XStack width={"100%"} justifyContent={"space-between"}>
          {businessModule.renderBlock({})}
        </XStack>
      </View>
    );
  },
);
